import { Injectable, WritableSignal, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { filter, single, take, takeUntil } from 'rxjs';
import { silentRequest } from 'src/app/auth-config';
import {
  INotification,
  NotificationSystemService,
} from 'src/app/common-components/services/common-notification-system.service';
import { AppbarService } from 'src/app/components/appbar/appbar-service';
import { AppConstants } from 'src/app/constants/app-constants';
import { Roles } from 'src/app/constants/app-types';
import {
  AttendantRequest,
  AttendantResponseModel,
  ProviderDirectoryFunctionService,
} from 'src/app/services/providerdirectoryapi/providerdirectoryfunctionapi.services';
import { b2c_config } from 'src/environments/environment';

import { AttendantProfileService } from '../../services/attendant/attendant-profile.service';
import { CertificatesService } from '../../services/reference-data/certificates.service';
import { LanguagesService } from '../../services/reference-data/languages.service';
import { ServicesService } from '../../services/reference-data/services.service';
import { WorkPreferencesService } from '../../services/reference-data/work-preferences.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private _token: WritableSignal<any> = signal(undefined);
  private _id: WritableSignal<string> = signal(undefined);
  private _email: WritableSignal<string> = signal(undefined);
  private role: WritableSignal<Roles[]> = signal(['User']);
  private roleHighest: WritableSignal<Roles> = signal('User');
  private _entity: WritableSignal<object> = signal(undefined);
  private _load: WritableSignal<boolean> = signal(false);
  private isAdminUser: WritableSignal<boolean> = signal(false);
  private isAttendantUser: WritableSignal<boolean> = signal(false);
  private isPublicUser: WritableSignal<boolean> = signal(true);

  private newEmail: string;

  constructor(
    private router: Router,
    private notificationSystemService: NotificationSystemService,
    private authService: MsalService,
    private appbarService: AppbarService,
    private msalBroadcastService: MsalBroadcastService,
    private attendantProfileService: AttendantProfileService,
    private providerDirectoryFunctionService: ProviderDirectoryFunctionService,
    private servicesService: ServicesService,
    private certificatesService: CertificatesService,
    private workPreferencesService: WorkPreferencesService,
    private languagesService: LanguagesService
  ) {
    this.msalBroadcastService.inProgress$
      .pipe(filter((status: InteractionStatus) => status === InteractionStatus.None))
      .subscribe({
        next: () => {
          this.getTokenInfo();
        },
        error: error => {
          if (error.message) {
            const notification: INotification = { type: 'error', content: error.message };
            this.notificationSystemService.setNotification(notification);
          }
        },
      });

    this.msalBroadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS))
      .subscribe({
        next: (result: EventMessage) => {
          this.servicesService.fetchData();
          this.certificatesService.fetchData();
          this.workPreferencesService.fetchData();
          this.languagesService.fetchData();
          this._token.set(result.payload);
          this._id.set(result.payload['idTokenClaims'].sub);
          this.updateRole(result.payload['idTokenClaims'].role);
          if (this.newEmail === undefined) {
            this._email.set(result.payload['idTokenClaims'].email);
            this.newEmail = result.payload['idTokenClaims'].email;
            this.checkAccount();
          }
        },
        error: error => {
          if (error.message) {
            const notification: INotification = { type: 'error', content: error.message };
            this.notificationSystemService.setNotification(notification);
          }
        },
      });

    this.msalBroadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS))
      .subscribe({
        next: (result: EventMessage) => {
          switch (result.payload['idTokenClaims']?.acr) {
            case b2c_config.change_signin_name_policy.toLowerCase():
              if (this.newEmail === undefined) {
                this._id.set(result.payload['idTokenClaims'].sub);
                this._email.set(result.payload['idTokenClaims'].email);
                this.newEmail = result.payload['idTokenClaims'].email;
                this.checkAccount();
              }
              break;
            case b2c_config.password_reset_policy.toLowerCase():
              const txt =
                AppConstants.selectedLanguage === 'en'
                  ? 'Password Updated Successfully'
                  : 'Contraseña Actualizado con Exito';
              const notification: INotification = { type: 'success', content: txt };
              this.notificationSystemService.setNotification(notification);
              this.router.navigate(['/Attendant/Edit']);
              break;
          }
        },
        error: error => {
          if (error.message) {
            const notification: INotification = { type: 'error', content: error.message };
            this.notificationSystemService.setNotification(notification);
          }
        },
      });
  }

  public getLoad(): WritableSignal<boolean> {
    return this._load;
  }

  public setLoad(data: boolean) {
    this._load.set(data);
  }

  public getId(): WritableSignal<string> {
    return this._id;
  }

  public getIdNow(): string {
    return this._id();
  }

  public getEmail(): WritableSignal<string> {
    return this._email;
  }

  public getEmailNow(): string {
    return this._email();
  }

  public getToken(): WritableSignal<any> {
    return this._token;
  }

  public getTokenNow(): any {
    return this._token();
  }

  public getRoles(): WritableSignal<Roles[]> {
    return this.role;
  }

  public getIsAdminSignal(): WritableSignal<boolean> {
    return this.isAdminUser;
  }

  public getIsAdmin(): boolean {
    return this.isAdminUser();
  }

  public setIsAdminSignal(data: boolean) {
    this.isAdminUser.set(data);
  }

  public getIsAttendantSignal(): WritableSignal<boolean> {
    return this.isAttendantUser;
  }

  public setIsAttendantSignal(data: boolean) {
    this.isAttendantUser.set(data);
  }

  public getIsPublicUserSignal(): WritableSignal<boolean> {
    return this.isPublicUser;
  }

  public setIsPublicUserSignal(data: boolean) {
    this.isPublicUser.set(data);
  }

  public getRoleHighest(): WritableSignal<Roles> {
    return this.roleHighest;
  }

  public getRoleHighestNow(): Roles {
    return this.roleHighest();
  }

  private getTokenInfo() {
    silentRequest.account = this.authService.instance.getAllAccounts()[0];
    if (silentRequest.account !== undefined) {
      if (silentRequest.account.idTokenClaims !== undefined) {
        silentRequest.authority =
          `https://${b2c_config.authority}/${b2c_config.directory}/` + silentRequest.account.idTokenClaims.acr;
      }
      silentRequest.forceRefresh = true;
      this.authService
        .acquireTokenSilent(silentRequest)
        .pipe(single())
        .subscribe({
          next: data => {
            if (data !== undefined) {
              this._token.set(data);
              this._id.set(data.idTokenClaims['sub']);
              this.updateRole(data.idTokenClaims['role']);
            }
          },
          error: error => {
            if (error.message) {
              const notification: INotification = { type: 'error', content: error.message };
              this.notificationSystemService.setNotification(notification);
            }
          },
          complete: () => {
            this._load.set(false);
          },
        });
    }
  }

  public updateRole(data: Roles[]): void {
    if (data) {
      if (!Array.isArray(data)) {
        data = [data];
      }
      this.role.set(data);
      if (data.find(r => r === 'ProviderDirectoryAdmin')) {
        this.roleHighest.set('ProviderDirectoryAdmin');
        this.isAdminUser.set(true);
        this.isAttendantUser.set(false);
        this.isPublicUser.set(false);
        this.router.navigate(['/Admin/Attendants']);
      } else if (data.find(r => r === 'Attendant')) {
        this.roleHighest.set('Attendant');
        this.isAttendantUser.set(true);
        this.isAdminUser.set(false);
        this.isPublicUser.set(false);
        this.attendantProfileService.search(this._id());
        this.attendantProfileService.setIsAdmin(false);
        this.router.navigate(['/Attendant']);
      } else {
        this.roleHighest.set('User');
        this.isPublicUser.set(true);
        this.isAdminUser.set(false);
        this.isAttendantUser.set(false);
      }
      this.appbarService.setRole(this.roleHighest());
    }
  }

  private checkAccount() {
    this._load.set(true);
    this.providerDirectoryFunctionService
      .getAttendantById(this._id())
      .pipe(take(1))
      .subscribe({
        next: data => {
          if (this.roleHighest() !== 'ProviderDirectoryAdmin' && data.data.userId === '') {
            this.createAccount();
          } else if (this.roleHighest() !== 'ProviderDirectoryAdmin' && data.data.email !== this.newEmail) {
            this.changeEmail();
          }
        },
        error: error => {
          if (error.message) {
            const notification: INotification = { type: 'error', content: error.message };
            this.notificationSystemService.setNotification(notification);
          }
        },
        complete: () => {
          this._load.set(false);
        },
      });
  }

  private createAccount() {
    const request = new AttendantRequest();
    request.userId = this._id();
    request.email = this._email();
    this._load.set(true);
    this.providerDirectoryFunctionService
      .createAttendant(request)
      .pipe(take(1))
      .subscribe({
        next: (res: AttendantResponseModel) => {
          if (res) {
            if (!res.isSuccess) {
              const notification: INotification = { type: 'error', content: res.message };
              this.notificationSystemService.setNotification(notification);
            } else {
              this._entity.set(res.data);
              this.attendantProfileService.setSearchParameters(request.userId);
              this.router.navigate(['/Attendant/Edit']);
            }
          }
        },
        error: error => {
          if (error.message) {
            const notification: INotification = { type: 'error', content: error.message };
            this.notificationSystemService.setNotification(notification);
          }
        },
        complete: () => {
          this._load.set(false);
        },
      });
  }

  private changeEmail() {
    if (this._email()) {
      const request = new AttendantRequest();
      request.userId = this._id();
      request.email = this._email();
      request.emailUpdate = true;
      this._load.set(true);
      this.providerDirectoryFunctionService
        .updateAttendant(request)
        .pipe(take(1))
        .subscribe({
          next: (res: AttendantResponseModel) => {
            if (res) {
              if (!res.isSuccess) {
                const notification: INotification = { type: 'error', content: res.message };
                this.notificationSystemService.setNotification(notification);
              } else {
                this._entity.set(res.data);
                const txt =
                  AppConstants.selectedLanguage === 'en'
                    ? 'Email Updated Successfully'
                    : 'Correo Electronico Actualizado con Exito';
                const notification: INotification = { type: 'success', content: txt };
                this.notificationSystemService.setNotification(notification);
                this.router.navigate(['/Attendant/Edit']);
              }
            }
          },
          error: error => {
            if (error.message) {
              const notification: INotification = { type: 'error', content: error.message };
              this.notificationSystemService.setNotification(notification);
            }
          },
          complete: () => {
            this.newEmail = undefined;
            this._load.set(false);
          },
        });
    } else {
      this.router.navigate(['/Attendant/Edit']);
    }
  }

  public clear(): void {
    this._entity.set(null);
  }

  public clearEntity(): void {
    this._entity.set(null);
  }

  public clearLoad(): void {
    this._load.set(null);
  }
}
